import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, Link } from "react-router-dom";
import { ArrowUpLongIcon, Body2, Button, InfoBox, SpinnerLoader } from "@activecollab/components";
import ExternalEmptySlate from "~/components/EmptySlate/ExternalEmptySlate";
import { useAuth } from "~/components/Hooks/useAuth";
import PublicLayout from "~/pages/Layouts/PublicLayout";
import { useCompletePublicPaymentPayPal } from "~/repository/usePublicPayments";
import localizationService from "~/services/LocalizationService";
import { resolvePublicInvoiceLink } from "~/utils/utils";

export const PublicPaymentCompletedPage = () => {
  const { t } = useTranslation();
  const { loading } = useAuth();
  const [searchParams] = useSearchParams();
  const { mutate: completePayPalPayment, isError, isSuccess } = useCompletePublicPaymentPayPal();
  const [finished, setIsFinished] = useState(false);
  const [showEmptySlate, setShowEmptySlate] = useState(false);
  const [response, setResponse] = useState(null);
  useEffect(() => {
    if (!searchParams.get("token")) {
      return setShowEmptySlate(true);
    }
    const data = { token: searchParams.get("token") };
    if (searchParams.get("PayerID")) {
      data.payer_id = searchParams.get("PayerID");
    }
    if (searchParams.get("token") && !isSuccess && !loading) {
      completePayPalPayment(data, {
        onSuccess: (r) => {
          setResponse(r);
          setIsFinished(true);
        },
        onError: () => {
          setIsFinished(true);
        },
      });
    }
  }, [completePayPalPayment, isSuccess, loading, searchParams]);

  useEffect(() => {
    if (response?.language) {
      localizationService.configure({ language_id: response?.language?.id }, [response?.language], true);
    }
  }, [response?.language]);

  if (showEmptySlate) {
    return (
      <PublicLayout
        title={t("Not Found")}
        shouldShowTerms={false}
      >
        <ExternalEmptySlate isLoggedIn={false} />
      </PublicLayout>
    );
  }

  return finished ? (
    <PublicLayout
      shouldShowLogo
      paperClassName="tw-flex tw-flex-col tw-items-center tw-p-8 tw-w-[360px]"
      bodyClassName="tw-flex tw-items-center tw-justify-center"
    >
      {isSuccess && response?.payment?.status === "paid" && !response?.already_paid ? (
        <>
          <InfoBox
            type="success"
            title={t("Payment Completed!")}
            showIcon
          >
            <Body2>
              {t("Your payment of {{amount}} {{currencyCode}} has been successfully completed.", {
                amount: response?.payment?.amount,
                currencyCode: response?.currency?.code,
              })}
              {response?.created_by?.email ? (
                <>
                  <br />
                  <br />
                  {t("We'll send a confirmation email to: {{email}}.", { email: response?.created_by?.email })}
                </>
              ) : null}
            </Body2>
          </InfoBox>
          <Link to={resolvePublicInvoiceLink(response?.back_url)}>
            <Button
              variant="text colored"
              className="tw-mt-6"
            >
              {t("Continue To Invoice")}
              <ArrowUpLongIcon className="tw-rotate-90" />
            </Button>
          </Link>
        </>
      ) : null}
      {isSuccess && response?.payment?.status === "canceled" ? (
        <>
          <InfoBox
            type="note"
            title={t("Payment Canceled")}
            showIcon
          >
            <Body2>{t("You have canceled your payment.")}</Body2>
          </InfoBox>
          <Link to={resolvePublicInvoiceLink(response?.payment?.back_url)}>
            <Button
              variant="text colored"
              className="tw-mt-6"
            >
              <ArrowUpLongIcon className="-tw-rotate-90" />
              {t("Back To Invoice")}
            </Button>
          </Link>
        </>
      ) : null}
      {isSuccess && response?.payment?.status === "paid" && response?.already_paid ? (
        <>
          <InfoBox
            type="success"
            title={t("Payment Completed!")}
            showIcon
          >
            <Body2>{t("This invoice has already been paid.")}</Body2>
          </InfoBox>
          <Link to={resolvePublicInvoiceLink(response?.back_url)}>
            <Button
              variant="text colored"
              className="tw-mt-6"
            >
              {t("Continue To Invoice")}
              <ArrowUpLongIcon className="tw-rotate-90" />
            </Button>
          </Link>
        </>
      ) : null}
      {isError ? (
        <>
          <InfoBox
            type="warning"
            title={t("Payment Unsuccessful")}
            showIcon
          >
            <Body2>
              {t("Your payment has not been charged.")}
              {!response?.back_url ? (
                <>
                  <br />
                  <br />
                  {t("Please try again, by opening the provided link.")}
                </>
              ) : null}
            </Body2>
          </InfoBox>
          {response?.back_url ? (
            <Link to={resolvePublicInvoiceLink(response?.back_url)}>
              <Button
                variant="text colored"
                className="tw-mt-6"
              >
                <ArrowUpLongIcon className="-tw-rotate-90" />
                {t("Back To Invoice")}
              </Button>
            </Link>
          ) : null}
        </>
      ) : null}
    </PublicLayout>
  ) : (
    <PublicLayout
      shouldShowLogo
      paperClassName="tw-flex tw-flex-col tw-items-center tw-p-8 tw-w-[360px]"
      bodyClassName="tw-flex tw-items-center tw-justify-center"
    >
      <SpinnerLoader
        radius={12}
        stroke={2}
        data-testid="spinner-loader"
      />
    </PublicLayout>
  );
};

export default PublicPaymentCompletedPage;
